import {FC, useEffect, useRef, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {PdfDocumentType} from './PdfDocument.types';
import {Document, Page, pdfjs} from 'react-pdf/dist/umd/entry.webpack';
import {Box, Button, IconButton, Stack, Typography} from '@mui/material';
import {
    documentWrapperSx,
    layoutSx,
    pageCountSx,
    pageNavButtonSx,
    pageNavCountSx,
    pageNavSx,
    pageStageSx,
    railActionSx,
    railHeadSx,
    railLabelSx,
    railRowSx,
    railValueSx,
    sectionBadgeSx,
    sectionTitleSx,
    SubmitBtnSx,
    titleSx,
    uploadSx,
    wrapperSx,
} from './PdfDocument.sx';
import {actionButtonSx} from '../actionButton.sx';
import {FormattedMessage, useIntl} from 'react-intl';
import {ArrowBackIosNew, ArrowForwardIos, DescriptionOutlined, Download} from '@mui/icons-material';
import DropZone from '../DropZone';
import {useAppDispatch} from '../../../app/hooks';
import {SubmitButton} from '../SubmitButton';
import {AppDispatch} from '../../../types';
import {preloaderChangeStatus} from '../../../stores/common/commonSlice';
import {store} from '../../../app/store';
import {contractsApi} from '../../../api';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

/**
 * How wide the page is drawn, and therefore how sharp it is.
 *
 * react-pdf rasterises at the width it is given, multiplied by the device pixel ratio. Given none it
 * used the PDF's own size — 595px for A4 — and the stylesheet then stretched that bitmap across a
 * card three times wider, which is what made the contract look pixelated.
 *
 * The cap is here because the page is a document, not a poster: past roughly A4 at 130% the lines
 * get no clearer, the reader just scrolls more. Below the cap the page follows the card.
 */
const PAGE_MAX_WIDTH = 820;

/** The card's own padding around the page: the stage's 24 either side, the document's 60. */
const PAGE_GUTTERS = 168;

const handleSubmit =
    (
        dispatch: AppDispatch,
        businessId: number | undefined,
        contractId: number | undefined,
        file?: File[] | null
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                if (file && contractId && businessId) {
                    const formData = new FormData();
                    formData.append('file', file[0]);
                    await store
                        .dispatch(
                            contractsApi.endpoints.uploadContract.initiate({
                                contractId,
                                businessId,
                                file: formData,
                            })
                        )
                        .unwrap();
                }
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const PdfDocument: FC<PdfDocumentType> = ({
                                                     pdfLink,
                                                     userName,
                                                     businessId,
                                                     contractId,
                                                 }) => {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const canUploadContract = useCan('contracts.createManual');

    const stageRef = useRef<HTMLDivElement | null>(null);
    const [pageWidth, setPageWidth] = useState<number>(PAGE_MAX_WIDTH);

    // Re-rasterise when the card changes width — a sidebar collapse or a window resize otherwise
    // leaves the old bitmap scaled by the browser, which is the blur this whole thing is about.
    useEffect(() => {
        const stage = stageRef.current;
        if (!stage || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            const available = entry.contentRect.width - PAGE_GUTTERS;
            setPageWidth(Math.max(320, Math.min(PAGE_MAX_WIDTH, available)));
        });
        observer.observe(stage);
        return () => observer.disconnect();
    }, [pdfLink]);

    const [file, setFile] = useState<File[] | null>(null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState<number>(1);

    const onDocumentLoadSuccess = ({numPages}: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handleNextPage = () => {
        setPageNumber(pageNumber + 1);
    };

    const handlePrevPage = () => {
        setPageNumber(pageNumber - 1);
    };

    const handleAddFile = (draggedFile: File[] | null) => {
        setFile(draggedFile);
    };

    const handleDownloadContract = () => {
        if (pdfLink) {
            const tempLink = document.createElement('a');
            tempLink.href = pdfLink;
            tempLink.setAttribute('download', `${userName}_contract.pdf`);
            tempLink.click();
            // window.URL.revokeObjectURL(tempLink.href)
        }
    };

    const fileName = `${userName}_contract.pdf`;

    const documentCard = (
        <Box sx={wrapperSx}>
            <Box sx={titleSx}>
                <Box sx={sectionBadgeSx}>
                    <DescriptionOutlined/>
                </Box>
                <Typography sx={sectionTitleSx}>
                    <FormattedMessage id="contract"/>
                </Typography>
                {Boolean(numPages) && (
                    <Typography sx={pageCountSx}>
                        {intl.formatMessage({id: 'pagesCount'}, {count: numPages ?? 0})}
                    </Typography>
                )}
            </Box>
            {!pdfLink && (
                <Box sx={uploadSx}>
                    <Typography
                        variant="body1"
                        sx={{mb: 1}}
                    >
                        {intl.formatMessage({id: 'attachedFile'})}
                    </Typography>
                    <DropZone
                        handleFile={handleAddFile}
                        isImage={false}
                    />
                    {canUploadContract && (
                        <Stack sx={SubmitBtnSx}>
                            <SubmitButton
                                text="submit"
                                isSubmitting={!file}
                                handler={handleSubmit(dispatch, businessId, contractId, file)}
                            />
                        </Stack>
                    )}
                </Box>
            )}
            {pdfLink && (
                <Box
                    ref={stageRef}
                    sx={{...pageStageSx as object, ...documentWrapperSx as object}}
                >
                    <Document
                        file={pdfLink}
                        renderMode="canvas"
                        onLoadSuccess={onDocumentLoadSuccess}
                    >
                        <Page
                            pageNumber={pageNumber}
                            width={pageWidth}
                            className="pdfPage"
                            renderAnnotationLayer={false}
                        />
                    </Document>
                </Box>
            )}
            {pdfLink && (numPages ?? 1) > 1 && (
                // In the footer rather than floating over the sheet: it appeared on hover, so a
                // reader had no way of knowing a second page existed.
                <Box sx={pageNavSx}>
                    <IconButton
                        sx={pageNavButtonSx}
                        onClick={handlePrevPage}
                        disabled={pageNumber === 1}
                    >
                        <ArrowBackIosNew sx={{fontSize: '14px'}}/>
                    </IconButton>
                    <Typography sx={pageNavCountSx}>
                        {pageNumber} / {numPages}
                    </Typography>
                    <IconButton
                        sx={pageNavButtonSx}
                        onClick={handleNextPage}
                        disabled={pageNumber === numPages}
                    >
                        <ArrowForwardIos sx={{fontSize: '14px'}}/>
                    </IconButton>
                </Box>
            )}
        </Box>
    );

    // The file's own facts. The card above this one is about the agreement — who signed it and when —
    // so nothing here repeats it: this column is about the document that carries it.
    const fileCard = (
        <Box sx={wrapperSx}>
            <Typography sx={railHeadSx}>
                <FormattedMessage id="documentDetails"/>
            </Typography>
            <Box sx={railRowSx}>
                <Typography sx={railLabelSx}>
                    <FormattedMessage id="documentPages"/>
                </Typography>
                <Typography sx={railValueSx}>{numPages ?? '—'}</Typography>
            </Box>
            <Box sx={railRowSx}>
                <Typography sx={railLabelSx}>
                    <FormattedMessage id="documentFile"/>
                </Typography>
                <Typography sx={railValueSx}>{fileName}</Typography>
            </Box>
            <Box sx={railActionSx}>
                <Button
                    variant="contained"
                    sx={actionButtonSx('32px', 'secondary')}
                    startIcon={<Download/>}
                    onClick={handleDownloadContract}
                >
                    <FormattedMessage id="download"/>
                </Button>
            </Box>
        </Box>
    );

    // Nothing attached yet: there is no document to put beside anything, so the card stands alone.
    if (!pdfLink) {
        return documentCard;
    }

    return (
        <Box sx={layoutSx}>
            {documentCard}
            {fileCard}
        </Box>
    );
};
